import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Pencil, Save, X, Plus, Trash2 } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProjectsManager = () => {
  const [projects, setProjects] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newProject, setNewProject] = useState({
    title: '',
    category: '',
    image: '',
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cms/projects`);
      setProjects(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast({
        title: "Error",
        description: "Failed to load projects",
        variant: "destructive"
      });
      setLoading(false);
    }
  };

  const handleEdit = (project) => {
    setEditingId(project.project_id);
    setEditData({ ...project });
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditData({});
  };

  const handleSave = async (projectId) => {
    try {
      await axios.put(`${BACKEND_URL}/api/cms/projects/${projectId}`, {
        title: editData.title,
        category: editData.category,
        image: editData.image
      });

      toast({
        title: "Success",
        description: "Project updated successfully"
      });

      setEditingId(null);
      fetchProjects();
    } catch (error) {
      console.error('Error updating project:', error);
      toast({
        title: "Error",
        description: "Failed to update project",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/cms/projects/${projectId}`);
      toast({
        title: "Success",
        description: "Project deleted successfully"
      });
      fetchProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
      toast({
        title: "Error",
        description: "Failed to delete project",
        variant: "destructive"
      });
    }
  };

  const handleAddProject = async () => {
    try {
      const nextId = projects.length > 0 ? Math.max(...projects.map(p => p.project_id)) + 1 : 1;
      await axios.post(`${BACKEND_URL}/api/cms/projects`, {
        project_id: nextId,
        ...newProject,
        order: nextId
      });

      toast({
        title: "Success",
        description: "Project added successfully"
      });

      setShowAddForm(false);
      setNewProject({ title: '', category: '', image: '' });
      fetchProjects();
    } catch (error) {
      console.error('Error adding project:', error);
      toast({
        title: "Error",
        description: "Failed to add project",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading projects...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-light text-[#2d2d2d]">Projects Management</h1>
        <Button
          onClick={() => setShowAddForm(!showAddForm)}
          className="bg-green-600 hover:bg-green-700 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add New Project
        </Button>
      </div>

      {showAddForm && (
        <div className="bg-white p-6 rounded-lg shadow-lg mb-6">
          <h3 className="text-xl font-light text-[#2d2d2d] mb-4">Add New Project</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Title</label>
              <Input
                value={newProject.title}
                onChange={(e) => setNewProject({ ...newProject, title: e.target.value })}
                placeholder="Project title"
              />
            </div>
            <div>
              <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Category</label>
              <Input
                value={newProject.category}
                onChange={(e) => setNewProject({ ...newProject, category: e.target.value })}
                placeholder="e.g., Residential, Commercial, Mixed Use"
              />
            </div>
            <div>
              <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Image URL</label>
              <Input
                value={newProject.image}
                onChange={(e) => setNewProject({ ...newProject, image: e.target.value })}
                placeholder="https://..."
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleAddProject} className="bg-green-600 hover:bg-green-700 text-white">
                <Save className="w-4 h-4 mr-2" />
                Add Project
              </Button>
              <Button onClick={() => setShowAddForm(false)} variant="outline">
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {projects.map((project) => (
          <div key={project.project_id} className="bg-white p-6 rounded-lg shadow-lg">
            {editingId === project.project_id ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Title</label>
                  <Input
                    value={editData.title}
                    onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Category</label>
                  <Input
                    value={editData.category}
                    onChange={(e) => setEditData({ ...editData, category: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Image URL</label>
                  <Input
                    value={editData.image}
                    onChange={(e) => setEditData({ ...editData, image: e.target.value })}
                  />
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => handleSave(project.project_id)} className="bg-green-600 hover:bg-green-700 text-white">
                    <Save className="w-4 h-4 mr-2" />
                    Save
                  </Button>
                  <Button onClick={handleCancel} variant="outline">
                    <X className="w-4 h-4 mr-2" />
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                <img src={project.image} alt={project.title} className="w-full h-48 object-cover rounded-lg mb-4" />
                <h3 className="text-xl font-light text-[#2d2d2d] mb-2">{project.title}</h3>
                <p className="text-[#2d2d2d] font-light mb-4">{project.category}</p>
                <div className="flex gap-2">
                  <Button onClick={() => handleEdit(project)} className="bg-[#0A1A50] hover:bg-[#060F2E] text-white">
                    <Pencil className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                  <Button onClick={() => handleDelete(project.project_id)} variant="outline" className="text-red-600 hover:text-red-700">
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProjectsManager;